import { getFileExtension, isObject } from "wombat-global/src/utilities";
// oxlint-disable-next-line import/no-unresolved
import CreateFileWorker from "./file-system.serviceWorker?worker";
import { BrowserFileSystemRef } from "wombat-global/src/typings";

// utilities
import { isErrorName } from "../utilities/requestErrors";

// types
type FilesList = { path: string[]; h: FileSystemHandle };

async function removeIfPresent(operation: () => Promise<void>): Promise<void> {
  try {
    await operation();
  } catch (error: unknown) {
    if (!isErrorName(error, "NotFoundError")) {
      throw error;
    }
  }
}

class WombatFileSystem {
  constructor() {}

  async getRootDirectory() {
    return navigator.storage.getDirectory();
  }

  /**
   * get the directory handler for the path provided
   */
  async cd(...dirs: string[]) {
    let _head = await this.getRootDirectory();
    while (dirs.length) {
      const _next = dirs.shift();
      if (_next) {
        _head = await _head.getDirectoryHandle(_next);
      }
    }
    return _head;
  }

  /**
   * return the fileObject
   */
  async readFile(dir: string | string[], name: string) {
    const _dir = await this.mkdir(...(Array.isArray(dir) ? dir : [dir]));
    const fileHandler = await _dir.getFileHandle(name);
    return await fileHandler.getFile();
  }

  /**
   * stream file into Origin specific file system. This utility will create the directories needed
   */
  async streamFile(
    dir: string | string[],
    name: string,
    file: File,
  ): Promise<BrowserFileSystemRef> {
    const _name = `${name}-${this.getFileName(file)}`;
    /**
     * @todo for older phones we need to push the streaming of the file into a
     * worker so we can use the syncWriteFile api thats accessible in IOS < 26
     */
    // const _dir = await this.mkdir(...(Array.isArray(dir) ? dir : [dir]));
    // const fileHandler = await _dir.getFileHandle(_name, { create: true });
    // const _writeStream = await fileHandler.createWritable();
    // await file.stream().pipeTo(_writeStream);

    const buffer = await file.arrayBuffer();
    const createFile = new CreateFileWorker({ name: _name });

    try {
      await new Promise<void>((resolve, reject) => {
        createFile.addEventListener(
          "message",
          (event: MessageEvent<unknown>) => {
            if (isObject(event.data) && event.data.error) {
              reject(event.data.error);
              return;
            }
            resolve();
          },
          { once: true },
        );
        createFile.addEventListener(
          "error",
          () => {
            reject(new Error("Could not save the attachment on this device."));
          },
          { once: true },
        );
        createFile.postMessage({
          buffer,
          name: _name,
          dir: Array.isArray(dir) ? dir : [dir],
        });
      });
    } finally {
      createFile.terminate();
    }

    const fileSystemRef: BrowserFileSystemRef = {
      path: Array.isArray(dir) ? dir : [dir],
      type: "file",
      name: _name,
      size: file.size,
      fileType: file.type,
    };
    return fileSystemRef;
  }

  /**
   * list all files and directories
   */
  async ls(...dir: string[]): Promise<FilesList[]> {
    const _dir = await this.cd(...dir);
    const res: FilesList[] = [];

    for await (const [name, handle] of _dir.entries()) {
      res.push({ path: [...dir, name], h: handle });
    }
    return res;
  }

  /**
   * recursively list files and all directories and files within those directories (all the way down)
   */
  async lsr(...dir: string[]): Promise<FilesList[]> {
    const _dir = await this.cd(...dir);
    const res: FilesList[] = [];

    for await (const [name, handle] of _dir.entries()) {
      if (handle.kind === "directory") {
        const sub = await this.lsr(...dir, name);
        res.push(...sub);
      } else {
        res.push({ path: [...dir, name], h: handle });
      }
    }

    return res;
  }

  /**
   * remove all files and directories under this dir
   */
  async rmrf(...dir: string[]) {
    await removeIfPresent(async () => {
      const _dir = await this.cd(...dir);
      for await (const [name, h] of _dir.entries()) {
        if (h.kind === "directory") {
          await this.rmrf(...dir, name);
        } else {
          await removeIfPresent(() => _dir.removeEntry(name));
        }
      }
    });
  }

  /**
   * remove the specific file or directory
   */
  async rm(...dir: string[]) {
    const name = dir.pop();
    if (!name) {
      return;
    }

    await removeIfPresent(async () => {
      const _dir = await this.cd(...dir);
      await _dir.removeEntry(name);
    });
  }

  /**
   * make directory and all parents
   */
  async mkdir(...dirs: string[]) {
    let _head = await this.getRootDirectory();
    while (dirs.length) {
      const _next = dirs.shift();
      if (_next) {
        _head = await _head.getDirectoryHandle(_next, { create: true });
      }
    }
    return _head;
  }

  getFileName(f: File) {
    const ext = getFileExtension(f.name);
    return `${f.name
      .replace(ext, "")
      .replaceAll(" ", "_")
      .replaceAll(" ", "-")}_${f.lastModified.toString(32).slice(2)}${ext}`;
  }
}

const FileSystem = new WombatFileSystem();
export default FileSystem;
